import { z } from 'zod'
import { booleanoDeQuery, paginacaoSchema, textoObrigatorio } from './comum'
import type { ContatoTagDto } from './contatos'

/**
 * Empresas (CNPJ), como no RD Station.
 *
 * O contato é a pessoa, um número de WhatsApp; a empresa junta as pessoas.
 * Quem manda na carteira é a empresa: ligar um contato passa o contato para o
 * dono dela, e transferir a empresa leva todos os contatos junto.
 */

/**
 * Os dois dígitos verificadores do CNPJ.
 *
 * Não confirma que a empresa existe (isso pediria consulta à Receita, que é
 * serviço externo); confirma que ninguém trocou um número ao digitar. É o erro
 * que acontece de verdade, e sem esta conta ele só aparece quando a nota
 * fiscal volta.
 */
export function cnpjValido(digitos: string): boolean {
  if (!/^\d{14}$/.test(digitos) || /^(\d)\1{13}$/.test(digitos)) return false
  const conta = (base: string, pesos: number[]) => {
    const soma = pesos.reduce((acc, peso, i) => acc + Number(base[i]) * peso, 0)
    const resto = soma % 11
    return resto < 2 ? 0 : 11 - resto
  }
  const primeiro = conta(digitos, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
  const segundo = conta(digitos, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
  return primeiro === Number(digitos[12]) && segundo === Number(digitos[13])
}

export function formatarCnpj(digitos: string | null): string | null {
  if (!digitos) return null
  if (digitos.length !== 14) return digitos
  return digitos.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

/** Texto opcional: vazio vira `null`, para o banco não guardar "" no lugar de nada. */
function opcional(max: number, rotulo: string) {
  return z
    .string()
    .trim()
    .max(max, `${rotulo} passa de ${max} caracteres`)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null))
}

export const empresaSchema = z.object({
  nome: textoObrigatorio('nome', 160),
  razaoSocial: opcional(200, 'razão social'),
  cnpj: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => (v ? v.replace(/\D/g, '') : null))
    .refine((v) => v === null || cnpjValido(v), 'CNPJ inválido: confira os 14 números.'),
  telefone: opcional(30, 'telefone'),
  email: z
    .union([z.literal(''), z.string().trim().email('e-mail inválido').max(160)])
    .optional()
    .nullable()
    .transform((v) => (v ? v.toLowerCase() : null)),
  site: opcional(200, 'site'),
  endereco: opcional(200, 'endereço'),
  bairro: opcional(100, 'bairro'),
  cidade: opcional(100, 'cidade'),
  uf: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => (v ? v.toUpperCase() : null))
    .refine((v) => v === null || /^[A-Z]{2}$/.test(v), 'UF são duas letras, como MG.'),
  cep: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => (v ? v.replace(/\D/g, '') : null))
    .refine((v) => v === null || v.length === 8, 'CEP tem 8 números.'),
  observacoes: z
    .string()
    .trim()
    .max(4000, 'observações passam de 4000 caracteres')
    .optional()
    .nullable()
    .transform((v) => (v ? v : null)),
  vendedorId: z.string().uuid().optional().nullable(),
  tagIds: z.array(z.string().uuid()).default([]),
  /** Os campos customizados de empresa (ADR 0069); o servidor confere contra a definição. */
  campos: z.record(z.string(), z.unknown()).optional(),
})
export type EmpresaEntrada = z.input<typeof empresaSchema>

export const atualizarEmpresaSchema = empresaSchema.partial()

export const filtroEmpresasSchema = paginacaoSchema.extend({
  busca: z.string().trim().max(120).optional(),
  vendedorId: z.string().uuid().optional(),
  semVendedor: booleanoDeQuery(false),
  ordenar: z.enum(['nome', 'recentes']).default('nome'),
})

/** Ligar ou soltar um contato de uma empresa. `null` solta. */
export const vincularEmpresaSchema = z.object({
  empresaId: z.string().uuid().nullable(),
})

/** A linha da lista de empresas. */
export interface EmpresaResumoDto {
  id: string
  nome: string
  razaoSocial: string | null
  /** Já formatado: 12.345.678/0001-90. */
  cnpj: string | null
  cidade: string | null
  uf: string | null
  vendedorId: string | null
  vendedorNome: string | null
  contatos: number
  negociacoesAbertas: number
  totalGanho: number
  tags: ContatoTagDto[]
  /** Os campos customizados de empresa, que viram colunas na lista (ADR 0069). */
  campos: Record<string, unknown>
}

export interface ContatoDaEmpresaDto {
  id: string
  nome: string
  /** Já formatado ou mascarado pelo servidor (regra 1). */
  telefone: string | null
  vendedorId: string | null
  vendedorNome: string | null
  negociacoesAbertas: number
}

export interface NegociacaoDaEmpresaDto {
  id: string
  contatoId: string
  contatoNome: string
  titulo: string | null
  funilNome: string
  etapaNome: string
  valor: number | null
  status: 'aberta' | 'ganha' | 'perdida'
  abertaEm: string
}

/** A empresa inteira, para a janela de detalhe. */
export interface EmpresaDto extends EmpresaResumoDto {
  telefone: string | null
  telefoneMascarado: boolean
  email: string | null
  site: string | null
  endereco: string | null
  bairro: string | null
  cep: string | null
  observacoes: string | null
  listaDeContatos: ContatoDaEmpresaDto[]
  negociacoes: NegociacaoDaEmpresaDto[]
  criadoEm: string
  atualizadoEm: string
}
